// A connectome pack's activity runs (connectome-pack-v0 `activity/`): the index, the run the pack
// plays by default (`default_run` where it names one, as H01's 0.3.0 does, else the first), and what
// Methods says about that run, from its run.json. The viewport plays the run itself; the app reads
// these to name the run and its model.

/** `activity/index.json`. */
export interface ActivityIndex {
  runs: { id: string; title: string; path: string }[];
  /** The run to play when none is picked; it must be one of `runs`. */
  default_run?: string;
}

/** One run as Methods describes it. */
export interface RunInfo {
  id: string;
  title: string;
  /** What's real and what's simulated, in the run's own words ("Real wiring, simulated activity"). */
  label?: string;
  provenance: string;
  model: { name: string; implementation?: string; commit?: string; basis?: string };
  /** The input: what drives the run and how many of the neurons it drives, of the modelled ones. */
  input?: { description: string; neurons: number; modelled?: number };
  /** The connections the run keeps (and their synapses), of the pack's, and how its revision treats self-edges. */
  wiring?: { kept: number; of: number; synapses?: number; selfEdges?: string };
  /** The run against the same input with no connections (its matched control): neurons whose spike train
   *  changes and those that spike identically, and the spikes added and removed. */
  control?: { changed: number; same: number; added: number; removed: number };
  /** Mean rate by class over the run: E, I and untyped, as the run names them. */
  classes?: { name: string; neurons: number; active: number; rateHz: number }[];
  durationS: number;
  frameMs?: number;
  spikes?: number;
  seed?: number;
}

export interface Activity {
  index: ActivityIndex;
  /** The run the viewport is asked to play. */
  defaultRun: string | null;
  /** That run's run.json, as Methods shows it; null where it didn't load. */
  run: RunInfo | null;
}

/** The pack's default run: `default_run` where it's one of the runs, else the first; null for none. */
export function defaultRun(index: ActivityIndex | null | undefined): string | null {
  const runs = index?.runs ?? [];
  const named = index?.default_run;
  return (named && runs.some((r) => r.id === named) ? named : runs[0]?.id) ?? null;
}

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
type Raw = Record<string, unknown>;
const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {});

/** Methods' reading of a run.json. The stimulus list itself is dropped: Methods counts it. */
export function runInfo(entry: { id: string; title: string }, raw: unknown): RunInfo {
  const r = obj(raw);
  const model = obj(r['model']);
  const stimulus = obj(r['stimulus']);
  const subset = obj(r['neuronal_subset']);
  const matched = obj(r['matched_control']);
  const byClass = obj(obj(r['metrics'])['by_class']);
  const driven = Array.isArray(stimulus['neurons']) ? stimulus['neurons'].length : undefined;
  const durationMs = num(r['duration_ms']) ?? 0;
  const label = str(r['label']);
  const implementation = str(model['implementation']);
  const commit = str(model['commit']);
  const basis = str(model['parameter_basis']);
  const description = str(stimulus['description']);
  const modelled = num(subset['modeled_neurons']);
  const kept = num(subset['retained_edges']);
  const of = num(subset['raw_edges']);
  const synapses = num(subset['retained_synapses']);
  const selfEdges = str(r['self_edge_policy']);
  const changed = num(matched['neurons_with_changed_spike_train']);
  const same = num(matched['exact_matching_neurons']);
  const added = num(matched['added_spike_events']);
  const removed = num(matched['removed_spike_events']);
  const classes = Object.entries(byClass).flatMap(([name, v]) => {
    const c = obj(v);
    const neurons = num(c['neurons']);
    const active = num(c['active']);
    const rateHz = num(c['rate_hz']);
    return neurons !== undefined && active !== undefined && rateHz !== undefined ? [{ name, neurons, active, rateHz }] : [];
  });
  const frameMs = num(r['frame_ms']);
  const spikes = num(r['spikes']);
  const seed = num(r['seed']);
  return {
    id: str(r['id']) ?? entry.id,
    title: entry.title,
    ...(label ? { label } : {}),
    provenance: str(r['provenance']) ?? 'simulated',
    model: { name: str(model['name']) ?? 'Unnamed model', ...(implementation ? { implementation } : {}), ...(commit ? { commit } : {}), ...(basis ? { basis } : {}) },
    ...(description && driven !== undefined ? { input: { description, neurons: driven, ...(modelled !== undefined ? { modelled } : {}) } } : {}),
    ...(kept !== undefined && of !== undefined ? { wiring: { kept, of, ...(synapses !== undefined ? { synapses } : {}), ...(selfEdges ? { selfEdges } : {}) } } : {}),
    ...(changed !== undefined && same !== undefined && added !== undefined && removed !== undefined ? { control: { changed, same, added, removed } } : {}),
    ...(classes.length ? { classes } : {}),
    durationS: durationMs / 1e3,
    ...(frameMs !== undefined ? { frameMs } : {}),
    ...(spikes !== undefined ? { spikes } : {}),
    ...(seed !== undefined ? { seed } : {}),
  };
}

/** `activity/index.json` from a pack's directory; null where the pack has none. */
export async function activityIndex(base: string, fetchFn: typeof fetch = fetch): Promise<ActivityIndex | null> {
  const res = await fetchFn(`${base}activity/index.json`).catch(() => null);
  if (!res?.ok) return null;
  const j = obj(await res.json().catch(() => null));
  const runs = Array.isArray(j['runs']) ? (j['runs'] as unknown[]).map(obj).filter((r) => str(r['id']) && str(r['path'])) : [];
  const named = str(j['default_run']);
  return { runs: runs.map((r) => ({ id: r['id'] as string, title: str(r['title']) ?? (r['id'] as string), path: r['path'] as string })), ...(named ? { default_run: named } : {}) };
}

/** A pack's activity: its index, its default run and that run's run.json; null for a pack with no runs. */
export async function loadActivity(base: string, fetchFn: typeof fetch = fetch): Promise<Activity | null> {
  const index = await activityIndex(base, fetchFn);
  const id = defaultRun(index);
  if (!index || !id) return null;
  const entry = index.runs.find((r) => r.id === id)!;
  const res = await fetchFn(base + entry.path).catch(() => null);
  const raw = res?.ok ? await res.json().catch(() => null) : null;
  return { index, defaultRun: id, run: raw ? runInfo(entry, raw) : null };
}
